import { z } from 'zod';

import { TAG_COLORS, type TagColor } from './contacts.ts';

/*
 * The agenda: calls, visits, meetings and tasks, linked to the deal, contact or
 * property that motivated them. A private activity is personal and never linked.
 * Rules shared by the API and the web forms. Messages are shown to users (Spanish).
 */

export const ACTIVITY_KINDS = {
  call: 'Llamada',
  whatsapp: 'WhatsApp',
  email: 'Correo',
  visit: 'Visita',
  meeting: 'Reunión',
  task: 'Tarea',
  other: 'Otro',
} as const;
export type ActivityKind = keyof typeof ACTIVITY_KINDS;

/** What a visit result does to the property in the deal (HU-AGE-07). */
export const RESULT_EFFECTS = {
  none: 'Nada',
  favorite: 'La marca como favorita',
  visited: 'La marca como visitada',
  discarded: 'La marca como descartada',
  second_visit: 'Visitada y propone una segunda visita',
} as const;
export type ResultEffect = keyof typeof RESULT_EFFECTS;

/** Types and results every new agency starts with (editable). */
export const DEFAULT_ACTIVITY_TYPES = [
  {
    kind: 'call',
    name: 'Llamada',
    color: 'blue',
    reminder: 15,
    results: [
      ['Contestó', 'none'],
      ['No contestó', 'none'],
      ['Número equivocado', 'none'],
      ['Interesado', 'none'],
      ['No interesado', 'none'],
    ],
  },
  {
    kind: 'whatsapp',
    name: 'WhatsApp',
    color: 'green',
    reminder: 15,
    results: [
      ['Respondió', 'none'],
      ['Sin respuesta', 'none'],
      ['Interesado', 'none'],
      ['No interesado', 'none'],
    ],
  },
  {
    kind: 'email',
    name: 'Correo',
    color: 'slate',
    reminder: 15,
    results: [
      ['Respondió', 'none'],
      ['Sin respuesta', 'none'],
    ],
  },
  {
    kind: 'visit',
    name: 'Visita',
    color: 'orange',
    reminder: 30,
    results: [
      ['Le gustó', 'favorite'],
      ['Precio alto', 'visited'],
      ['Descarta', 'discarded'],
      ['Quiere segunda visita', 'second_visit'],
      ['No llegó', 'none'],
    ],
  },
  {
    kind: 'meeting',
    name: 'Reunión',
    color: 'violet',
    reminder: 30,
    results: [
      ['Realizada', 'none'],
      ['Reprogramada', 'none'],
      ['Cancelada', 'none'],
    ],
  },
  {
    kind: 'task',
    name: 'Tarea',
    color: 'teal',
    reminder: 15,
    results: [
      ['Hecha', 'none'],
      ['Cancelada', 'none'],
    ],
  },
] as const satisfies readonly {
  kind: ActivityKind;
  name: string;
  color: TagColor;
  reminder: number;
  results: readonly (readonly [string, ResultEffect])[];
}[];

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Máximo ${String(max)} caracteres`)
    .transform((v) => (v === '' ? null : v))
    .nullable();

/** Create and edit share the body. The title defaults to "Llamada con María...". */
export const activitySchema = z
  .strictObject({
    typeId: z.uuid('Elige el tipo'),
    title: optionalText(150).optional(),
    startsAt: z.iso.datetime({ offset: true, message: 'Fecha y hora no válidas' }),
    endsAt: z.iso.datetime({ offset: true }).nullable().default(null),
    allDay: z.boolean().default(false),
    assignedMembershipId: z.uuid('Responsable no válido').optional(),
    isPrivate: z.boolean().default(false),
    dealId: z.uuid().nullable().default(null),
    contactId: z.uuid().nullable().default(null),
    propertyId: z.uuid().nullable().default(null),
    note: optionalText(5000).default(null),
    /** Minutes before; null: no reminder; undefined: the type's default. */
    reminderMinutes: z.number().int().min(0).max(10080).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.isPrivate && (v.dealId ?? v.contactId ?? v.propertyId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['isPrivate'],
        message:
          'Una actividad privada es personal: no se vincula a negocios, contactos ni propiedades',
      });
    }
    if (v.endsAt && new Date(v.endsAt).getTime() < new Date(v.startsAt).getTime()) {
      ctx.addIssue({
        code: 'custom',
        path: ['endsAt'],
        message: 'Debe terminar después de empezar',
      });
    }
  });
export type ActivityInput = z.input<typeof activitySchema>;
export type ActivityData = z.output<typeof activitySchema>;

export const completeActivitySchema = z.strictObject({
  resultId: z.uuid().nullable().default(null),
  comment: optionalText(1000).default(null),
});
export type CompleteActivityInput = z.input<typeof completeActivitySchema>;

export const ACTIVITY_VIEWS = {
  today: 'Hoy',
  overdue: 'Vencidas',
  upcoming: 'Próximas',
  done: 'Completadas',
} as const;
export type ActivityView = keyof typeof ACTIVITY_VIEWS;

export const activityListQuerySchema = z.strictObject({
  view: z.enum(Object.keys(ACTIVITY_VIEWS) as [ActivityView]).default('today'),
  typeId: z.uuid().optional(),
  assignedMembershipId: z.uuid().optional(),
  link: z.enum(['deal', 'contact', 'property', 'none']).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export type ActivityListQuery = z.infer<typeof activityListQuerySchema>;

export const calendarQuerySchema = z
  .strictObject({
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
    /** Comma-separated membership ids (team calendar); default: only mine. */
    members: z
      .string()
      .max(2000)
      .transform((v) => v.split(',').filter(Boolean))
      .pipe(z.array(z.uuid()).max(50))
      .optional(),
  })
  .refine(
    (v) => new Date(v.to).getTime() - new Date(v.from).getTime() <= 45 * 86_400_000,
    'El rango máximo es de 45 días',
  );
export type CalendarQuery = z.output<typeof calendarQuerySchema>;

export const conflictQuerySchema = z.strictObject({
  startsAt: z.iso.datetime({ offset: true }),
  endsAt: z.iso.datetime({ offset: true }).optional(),
  assignedMembershipId: z.uuid().optional(),
  excludeId: z.uuid().optional(),
});

export const activityTypeSchema = z.strictObject({
  name: z.string().trim().min(1, 'Escribe el nombre').max(40),
  color: z.enum(TAG_COLORS).default('blue'),
  defaultReminderMinutes: z.number().int().min(0).max(10080).nullable().default(null),
});
export const activityTypeUpdateSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(40).optional(),
    color: z.enum(TAG_COLORS).optional(),
    defaultReminderMinutes: z.number().int().min(0).max(10080).nullable().optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'No hay cambios');
export const activityResultSchema = z.strictObject({
  name: z.string().trim().min(1, 'Escribe el resultado').max(60),
  effect: z.enum(Object.keys(RESULT_EFFECTS) as [ResultEffect]).default('none'),
});
export const activityResultUpdateSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(60).optional(),
    effect: z.enum(Object.keys(RESULT_EFFECTS) as [ResultEffect]).optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'No hay cambios');

/** Minutes of an activity without an end, for overlaps and the calendar. */
export const DEFAULT_ACTIVITY_MINUTES = 30;

/** "Llamada con María Hernández" / "Visita · P7K3M9Q" when no title is given. */
export function defaultActivityTitle(
  typeName: string,
  link: { contactName?: string | null; propertyCode?: string | null },
): string {
  if (link.contactName) return `${typeName} con ${link.contactName}`.slice(0, 150);
  if (link.propertyCode) return `${typeName} · ${link.propertyCode}`;
  return typeName;
}
